import type { MenuItem } from '../types';
import { graphqlRequest, GraphQLRequestError } from '../graphql/client';
import {
  AUTHORIZE_SENSITIVE_ACCESS_MUTATION,
  ACQUIRE_SCENARIO_EDIT_LOCK_MUTATION,
  ARCHIVE_SCENARIO_MUTATION,
  BOOTSTRAP_QUERY,
  DELETE_SCENARIO_MUTATION,
  LOGIN_MUTATION,
  LOGIN_WITH_PIN_MUTATION,
  LOGOUT_MUTATION,
  PRODUCTS_QUERY,
  PUSH_SCENARIO_SNAPSHOT_MUTATION,
  REDO_SCENARIO_HISTORY_MUTATION,
  RELEASE_SCENARIO_EDIT_LOCK_MUTATION,
  RENEW_SCENARIO_EDIT_LOCK_MUTATION,
  RESTORE_SCENARIO_MUTATION,
  REVALIDATE_SESSION_MUTATION,
  REQUEST_SCENARIO_EDIT_ACCESS_MUTATION,
  RESPOND_SCENARIO_EDIT_ACCESS_REQUEST_MUTATION,
  SAVE_SCENARIO_BASE_STATE_MUTATION,
  SCENARIO_BASE_STATE_QUERY,
  SCENARIO_EDIT_PRESENCE_QUERY,
  SCENARIO_HISTORY_QUERY,
  SCENARIO_LIBRARY_QUERY,
  SET_SCENARIO_RELEASE_PENDING_MUTATION,
  UNDO_SCENARIO_HISTORY_MUTATION,
  UPDATE_PROFILE_MUTATION,
  UPDATE_SCENARIO_MUTATION,
  UPDATE_USER_PREFERENCES_MUTATION,
} from '../graphql/documents';
import { getOrCreateDeviceId } from '../auth/device';
import { mapBootstrapScenarioToSnapshot, mapProductsToMenuItems, mapScenarioSummary } from './graphqlMappers';
import type {
  AuthSessionRecord,
  BootstrapPayload,
  LocalAuthUser,
  ProductRecord,
  RuntimeSyncState,
  ScenarioBaseStateRecord,
  ScenarioEditPresenceRecord,
  ScenarioHistoryStateRecord,
  ScenarioSnapshot,
  ScenarioStatus,
  ScenarioSummaryRecord,
  UserPreferences,
} from './types';

const DEFAULT_SCENARIO_NAME = 'Main Room';
const SAVE_DEBOUNCE_MS = 350;

export interface ConflictScenarioDraft {
  suggestedName: string;
  snapshot: ScenarioSnapshot;
}

export const SYNC_STATE_CHANGED_EVENT = 'tableArrangement:sync-state-changed';

const defaultSyncState: RuntimeSyncState = {
  status: 'idle',
  pendingCount: 0,
  conflictCount: 0,
  lastError: null,
  lastSyncedAt: null,
  lastConnectivityCheckAt: null,
  authRequired: false,
  conflictScenarioName: null,
  conflictRemoteVersion: null,
};

interface LoginResponse {
  login: {
    authenticated: boolean;
    deviceId: string;
    authMethod: 'password' | 'pin' | null;
    sensitiveAccessGranted: boolean;
    user: LocalAuthUser;
  };
}

interface LoginWithPinResponse {
  loginWithPin: {
    authenticated: boolean;
    deviceId: string;
    authMethod: 'password' | 'pin' | null;
    sensitiveAccessGranted: boolean;
    user: LocalAuthUser;
  };
}

interface BootstrapResponse {
  bootstrapState: BootstrapPayload;
}

interface RevalidateResponse {
  revalidateSession: {
    authenticated: boolean;
    deviceId: string | null;
    authMethod: 'password' | 'pin' | null;
    sensitiveAccessGranted: boolean;
    user: LocalAuthUser | null;
  };
}

interface AuthorizeSensitiveAccessResponse {
  authorizeSensitiveAccess: {
    authenticated: boolean;
    deviceId: string | null;
    authMethod: 'password' | 'pin' | null;
    sensitiveAccessGranted: boolean;
    user: LocalAuthUser | null;
  };
}

interface PushScenarioSnapshotResponse {
  pushScenarioSnapshot: {
    accepted: boolean;
    scenario: BootstrapPayload['scenario'];
    products: ProductRecord[];
    catalogVersion: number;
  };
}

interface UpdateProfileResponse {
  updateProfile: LocalAuthUser;
}

interface UpdateUserPreferencesResponse {
  updateUserPreferences: LocalAuthUser;
}

interface ScenarioHistoryResponse {
  scenarioHistory: ScenarioHistoryStateRecord;
}

interface ScenarioHistoryMutationPayload {
  scenario: BootstrapPayload['scenario'];
  history: ScenarioHistoryStateRecord;
}

interface UndoScenarioHistoryResponse {
  undoScenarioHistory: ScenarioHistoryMutationPayload;
}

interface RedoScenarioHistoryResponse {
  redoScenarioHistory: ScenarioHistoryMutationPayload;
}

interface ScenarioLibraryResponse {
  scenarioLibrary: ScenarioSummaryRecord[];
}

interface ProductsResponse {
  products: ProductRecord[];
}

interface UpdateScenarioResponse {
  updateScenario: BootstrapPayload['scenario'];
}

interface ArchiveScenarioResponse {
  archiveScenario: BootstrapPayload['scenario'];
}

interface RestoreScenarioResponse {
  restoreScenario: BootstrapPayload['scenario'];
}

interface DeleteScenarioResponse {
  deleteScenario: boolean;
}

interface ScenarioBaseStateResponse {
  scenarioBaseState: ScenarioBaseStateRecord | null;
}

interface SaveScenarioBaseStateResponse {
  saveScenarioBaseState: BootstrapPayload['scenario'];
}

interface ScenarioEditPresenceResponse {
  scenarioEditPresence: ScenarioEditPresenceRecord;
}

interface AcquireScenarioEditLockResponse {
  acquireScenarioEditLock: ScenarioEditPresenceRecord;
}

interface RenewScenarioEditLockResponse {
  renewScenarioEditLock: ScenarioEditPresenceRecord;
}

interface ReleaseScenarioEditLockResponse {
  releaseScenarioEditLock: ScenarioEditPresenceRecord;
}

interface RequestScenarioEditAccessResponse {
  requestScenarioEditAccess: ScenarioEditPresenceRecord;
}

interface SetScenarioReleasePendingResponse {
  setScenarioReleasePending: ScenarioEditPresenceRecord;
}

interface RespondScenarioEditAccessRequestResponse {
  respondScenarioEditAccessRequest: ScenarioEditPresenceRecord;
}

interface GraphQLRequestOptions {
  keepalive?: boolean;
}

type UserPreferencesUpdateInput = Partial<Omit<UserPreferences, 'lastScenarioName' | 'recentScenarioNames'>> & {
  lastScenarioName?: string | null;
  recentScenarioNames?: string[] | null;
};

interface PendingScenarioWrite {
  snapshot: ScenarioSnapshot;
  previousScenarioName: string | null;
  forceOverwrite: boolean;
  historyDescription: string | null;
}

let authStateCache: AuthSessionRecord | null = null;
let currentScenarioCache: ScenarioSnapshot | null = null;
let scenarioLibraryCache: ScenarioSummaryRecord[] = [];
let catalogCache: ProductRecord[] = [];
let syncStateCache: RuntimeSyncState = { ...defaultSyncState };
let latestConflictDraft: ConflictScenarioDraft | null = null;
let latestConflictScenarioName: string | null = null;
let latestConflictRemoteVersion: number | null = null;
let pendingScenarioWrites: PendingScenarioWrite[] = [];
let scenarioWriteTimer: number | null = null;
let scenarioWriteInFlight = false;
let currentScenarioWritePromise: Promise<RuntimeSyncState> = Promise.resolve({ ...defaultSyncState });
const lastSyncedVersionByScenario = new Map<string, number>();

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isBrowser() {
  return typeof window !== 'undefined';
}

export function clearObsoleteLocalPersistence(): void {
  if (!isBrowser()) {
    return;
  }

  const obsoleteKeys = [
    'tableArrangement_authState',
    'tableArrangement_lastScenario',
    'tableArrangement_recentScenarios',
    'tableArrangement_darkMode',
    'tableArrangement_lightTableColor',
    'tableArrangement_darkTableColor',
    'tableArrangement_lightWallColor',
    'tableArrangement_darkWallColor',
    'tableArrangement_defaultScenario',
  ];
  obsoleteKeys.forEach((key) => window.localStorage.removeItem(key));
  for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
    const key = window.localStorage.key(index);
    if (!key) {
      continue;
    }
    if (
      key.startsWith('historyEntries:') ||
      key.startsWith('historyCursor:') ||
      key.startsWith('tableArrangement_userDarkMode:')
    ) {
      window.localStorage.removeItem(key);
    }
  }
}

function defaultAuthState(deviceId: string): AuthSessionRecord {
  return {
    key: 'session',
    authenticated: false,
    authExpired: false,
    deviceId,
    authMethod: null,
    sensitiveAccessGranted: false,
    user: null,
    lastValidatedAt: null,
    lastSuccessfulOnlineAuthAt: null,
    lastActivityAt: null,
  };
}

function normalizeRuntimeSyncState(
  savedState: Partial<RuntimeSyncState> | null | undefined,
): RuntimeSyncState {
  return {
    ...defaultSyncState,
    ...(savedState ?? {}),
    pendingCount: typeof savedState?.pendingCount === 'number' ? savedState.pendingCount : 0,
    conflictCount: typeof savedState?.conflictCount === 'number' ? savedState.conflictCount : 0,
    conflictScenarioName:
      typeof savedState?.conflictScenarioName === 'string' ? savedState.conflictScenarioName : null,
    conflictRemoteVersion:
      typeof savedState?.conflictRemoteVersion === 'number' ? savedState.conflictRemoteVersion : null,
  };
}

function dispatchSyncStateChanged(state: RuntimeSyncState): void {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(new CustomEvent<RuntimeSyncState>(SYNC_STATE_CHANGED_EVENT, { detail: state }));
}

async function persistSyncState(state: RuntimeSyncState): Promise<void> {
  syncStateCache = normalizeRuntimeSyncState(state);
  dispatchSyncStateChanged(syncStateCache);
}

async function setSyncStateIdle(): Promise<RuntimeSyncState> {
  const nextState: RuntimeSyncState = {
    ...defaultSyncState,
    lastSyncedAt: new Date().toISOString(),
    lastConnectivityCheckAt: new Date().toISOString(),
  };
  await persistSyncState(nextState);
  return nextState;
}

function getPendingScenarioWriteCount(): number {
  return pendingScenarioWrites.length;
}

function getLatestPendingScenarioWrite(): PendingScenarioWrite | null {
  return pendingScenarioWrites[pendingScenarioWrites.length - 1] ?? null;
}

function hasPendingScenarioWrites(): boolean {
  return pendingScenarioWrites.length > 0;
}

function restorePendingScenarioWrites(writes: PendingScenarioWrite[]): void {
  pendingScenarioWrites = writes.map((write) => ({
    ...write,
    snapshot: clone(write.snapshot),
  }));
}

function getEffectivePendingDraft(): PendingScenarioWrite | null {
  const latestPendingWrite = getLatestPendingScenarioWrite();
  if (latestPendingWrite) {
    return latestPendingWrite;
  }

  if (latestConflictDraft && latestConflictScenarioName) {
    return {
      snapshot: clone(latestConflictDraft.snapshot),
      previousScenarioName: latestConflictScenarioName,
      forceOverwrite: false,
      historyDescription: null,
    };
  }

  return null;
}

function updateScenarioLibraryCache(scenarios: ScenarioSummaryRecord[]): void {
  scenarioLibraryCache = [...scenarios].sort((left, right) => {
    if (left.isDefault !== right.isDefault) {
      return left.isDefault ? -1 : 1;
    }

    const byUpdatedAt = right.updatedAt.localeCompare(left.updatedAt);
    if (byUpdatedAt !== 0) {
      return byUpdatedAt;
    }

    return left.name.localeCompare(right.name);
  });
}

function upsertScenarioSummary(summary: ScenarioSummaryRecord): void {
  updateScenarioLibraryCache([
    ...scenarioLibraryCache.filter((entry) => entry.id !== summary.id && entry.name !== summary.name),
    summary,
  ]);
}

function removeScenarioSummary(name: string): void {
  updateScenarioLibraryCache(scenarioLibraryCache.filter((entry) => entry.name !== name));
}

function setCurrentScenario(snapshot: ScenarioSnapshot | null): void {
  currentScenarioCache = snapshot ? clone(snapshot) : null;

  if (!snapshot) {
    return;
  }

  if (typeof snapshot.currentVersion === 'number') {
    lastSyncedVersionByScenario.set(snapshot.scenarioName, snapshot.currentVersion);
  }

}

function setCatalogCache(products: ProductRecord[]): void {
  catalogCache = clone(products);
}

function buildConflictSyncState(message: string, scenarioName: string | null, remoteVersion: number | null): RuntimeSyncState {
  return {
    status: 'conflict',
    pendingCount: 0,
    conflictCount: 1,
    lastError: message,
    lastSyncedAt: null,
    lastConnectivityCheckAt: new Date().toISOString(),
    authRequired: false,
    conflictScenarioName: scenarioName,
    conflictRemoteVersion: remoteVersion,
  };
}

function buildLockedSyncState(message: string): RuntimeSyncState {
  return {
    status: 'locked',
    pendingCount: getPendingScenarioWriteCount(),
    conflictCount: 0,
    lastError: message,
    lastSyncedAt: null,
    lastConnectivityCheckAt: new Date().toISOString(),
    authRequired: false,
    conflictScenarioName: null,
    conflictRemoteVersion: null,
  };
}

function buildFailedSyncState(message: string): RuntimeSyncState {
  return {
    status: 'failed',
    pendingCount: getPendingScenarioWriteCount(),
    conflictCount: 0,
    lastError: message,
    lastSyncedAt: null,
    lastConnectivityCheckAt: new Date().toISOString(),
    authRequired: false,
    conflictScenarioName: null,
    conflictRemoteVersion: null,
  };
}

function buildBlockedSyncState(message: string): RuntimeSyncState {
  return {
    status: 'blocked',
    pendingCount: getPendingScenarioWriteCount(),
    conflictCount: 0,
    lastError: message,
    lastSyncedAt: null,
    lastConnectivityCheckAt: new Date().toISOString(),
    authRequired: true,
    conflictScenarioName: null,
    conflictRemoteVersion: null,
  };
}

function getSuggestedCopyName(baseName: string): string {
  const trimmedBaseName = baseName.trim() || DEFAULT_SCENARIO_NAME;
  const existingNames = new Set(scenarioLibraryCache.map((scenario) => scenario.name));
  const initialName = `${trimmedBaseName}_copy`;
  if (!existingNames.has(initialName)) {
    return initialName;
  }

  let suffix = 2;
  while (existingNames.has(`${trimmedBaseName}_copy_${suffix}`)) {
    suffix += 1;
  }
  return `${trimmedBaseName}_copy_${suffix}`;
}

async function ensureAuthStateLoaded(): Promise<AuthSessionRecord> {
  if (authStateCache) {
    return authStateCache;
  }

  const deviceId = await getOrCreateDeviceId();
  authStateCache = defaultAuthState(deviceId);
  return authStateCache;
}

async function persistAuthState(state: AuthSessionRecord): Promise<void> {
  authStateCache = { ...state };
}

async function persistExpiredAuthState(): Promise<AuthSessionRecord> {
  const current = await ensureAuthStateLoaded();
  const nextState = defaultAuthState(current.deviceId);
  nextState.authExpired = true;
  await persistAuthState(nextState);

  if (hasPendingScenarioWrites()) {
    await persistSyncState(
      buildBlockedSyncState('Authentication expired. Re-login is required before saving can continue.'),
    );
  }

  return nextState;
}

async function applyBootstrapPayload(payload: BootstrapPayload, preferredScenarioName?: string): Promise<BootstrapPayload> {
  const currentAuth = await ensureAuthStateLoaded();
  const nextAuth: AuthSessionRecord = {
    ...currentAuth,
    authenticated: payload.session.authenticated,
    authExpired: false,
    deviceId: payload.session.deviceId ?? currentAuth.deviceId,
    authMethod: payload.session.authMethod ?? null,
    sensitiveAccessGranted: payload.session.sensitiveAccessGranted,
    user: payload.session.user,
    lastValidatedAt: new Date().toISOString(),
    lastSuccessfulOnlineAuthAt: payload.session.authenticated
      ? (currentAuth.lastSuccessfulOnlineAuthAt ?? new Date().toISOString())
      : null,
    lastActivityAt: payload.session.authenticated ? new Date().toISOString() : null,
  };
  await persistAuthState(nextAuth);

  if (!payload.session.authenticated) {
    setCurrentScenario(null);
    updateScenarioLibraryCache([]);
    setCatalogCache([]);
    latestConflictDraft = null;
    latestConflictScenarioName = null;
    latestConflictRemoteVersion = null;
    await persistSyncState({ ...defaultSyncState });
    return payload;
  }

  updateScenarioLibraryCache(payload.scenarios.map(mapScenarioSummary));
  setCatalogCache(payload.products);

  const nextScenario = mapBootstrapScenarioToSnapshot(payload.scenario);
  if (nextScenario) {
    setCurrentScenario(nextScenario);
  } else if (preferredScenarioName && currentScenarioCache?.scenarioName !== preferredScenarioName) {
    setCurrentScenario(null);
  }

  return payload;
}

async function bootstrapScenarioFromServer(scenarioName?: string): Promise<BootstrapPayload> {
  let data: BootstrapResponse;
  try {
    data = await graphqlRequest<BootstrapResponse>(BOOTSTRAP_QUERY, scenarioName ? { scenarioName } : undefined);
  } catch (error) {
    if (error instanceof GraphQLRequestError && error.code === 'UNAUTHENTICATED') {
      await persistExpiredAuthState();
    }
    throw error;
  }
  return applyBootstrapPayload(data.bootstrapState, scenarioName);
}

function normalizeAuthSessionFromResponse(session: {
  authenticated: boolean;
  deviceId: string | null;
  authMethod: 'password' | 'pin' | null;
  sensitiveAccessGranted: boolean;
  user: LocalAuthUser | null;
}): Promise<AuthSessionRecord> {
  return ensureAuthStateLoaded().then(async (currentAuth) => {
    const nextAuth: AuthSessionRecord = {
      ...currentAuth,
      authenticated: session.authenticated,
      authExpired: false,
      deviceId: session.deviceId ?? currentAuth.deviceId,
      authMethod: session.authMethod ?? null,
      sensitiveAccessGranted: session.sensitiveAccessGranted,
      user: session.user,
      lastValidatedAt: new Date().toISOString(),
      lastSuccessfulOnlineAuthAt: session.authenticated
        ? (currentAuth.lastSuccessfulOnlineAuthAt ?? new Date().toISOString())
        : null,
      lastActivityAt: session.authenticated ? new Date().toISOString() : null,
    };
    await persistAuthState(nextAuth);
    return nextAuth;
  });
}

async function flushPendingScenarioWrite(requestOptions?: GraphQLRequestOptions): Promise<RuntimeSyncState> {
  if (scenarioWriteTimer !== null && isBrowser()) {
    window.clearTimeout(scenarioWriteTimer);
    scenarioWriteTimer = null;
  }

  if (scenarioWriteInFlight) {
    return currentScenarioWritePromise;
  }

  const draft = pendingScenarioWrites[0] ?? null;
  if (!draft) {
    return setSyncStateIdle();
  }

  pendingScenarioWrites = pendingScenarioWrites.slice(1);
  scenarioWriteInFlight = true;
  await persistSyncState({
    status: 'syncing',
    pendingCount: getPendingScenarioWriteCount() + 1,
    conflictCount: 0,
    lastError: null,
    lastSyncedAt: syncStateCache.lastSyncedAt,
    lastConnectivityCheckAt: new Date().toISOString(),
    authRequired: false,
    conflictScenarioName: null,
    conflictRemoteVersion: null,
  });

  currentScenarioWritePromise = (async () => {
    const auth = await ensureAuthStateLoaded();
    if (!auth.user || !auth.authenticated) {
      restorePendingScenarioWrites([draft, ...pendingScenarioWrites]);
      const blockedState = buildBlockedSyncState('Authentication expired. Re-login is required before saving can continue.');
      await persistSyncState(blockedState);
      scenarioWriteInFlight = false;
      return blockedState;
    }

    const baseVersion = draft.forceOverwrite
      ? null
      : (lastSyncedVersionByScenario.get(draft.snapshot.scenarioName) ?? draft.snapshot.currentVersion ?? null);

    try {
      const data = await graphqlRequest<PushScenarioSnapshotResponse>(
        PUSH_SCENARIO_SNAPSHOT_MUTATION,
        {
          input: {
            scenarioName: draft.snapshot.scenarioName,
            previousScenarioName: draft.previousScenarioName,
            deviceId: auth.deviceId,
            snapshot: draft.snapshot,
            baseVersion,
            historyDescription: draft.historyDescription,
          },
        },
        requestOptions,
      );

      const persistedSnapshot = mapBootstrapScenarioToSnapshot(data.pushScenarioSnapshot.scenario);
      if (persistedSnapshot) {
        lastSyncedVersionByScenario.set(persistedSnapshot.scenarioName, persistedSnapshot.currentVersion ?? 1);
        upsertScenarioSummary({
          id: persistedSnapshot.scenarioId ?? persistedSnapshot.scenarioName,
          name: persistedSnapshot.scenarioName,
          status: persistedSnapshot.status,
          isDefault: persistedSnapshot.isDefault,
          currentVersion: persistedSnapshot.currentVersion ?? 1,
          updatedAt: persistedSnapshot.updatedAt,
          hasLocalSnapshot: false,
        });

        if (draft.previousScenarioName && draft.previousScenarioName !== persistedSnapshot.scenarioName) {
          removeScenarioSummary(draft.previousScenarioName);
        }

        pendingScenarioWrites = pendingScenarioWrites.map((queuedWrite) => (
          queuedWrite.snapshot.scenarioName === persistedSnapshot.scenarioName
            ? {
                ...queuedWrite,
                snapshot: {
                  ...queuedWrite.snapshot,
                  scenarioId: persistedSnapshot.scenarioId ?? queuedWrite.snapshot.scenarioId,
                  currentVersion: persistedSnapshot.currentVersion,
                  status: persistedSnapshot.status,
                  isDefault: persistedSnapshot.isDefault,
                },
              }
            : queuedWrite
        ));

        const latestPendingWrite = getLatestPendingScenarioWrite();
        const currentScenario = currentScenarioCache;
        const currentScenarioMatches = currentScenario?.scenarioName === persistedSnapshot.scenarioName;
        if (currentScenarioMatches && latestPendingWrite?.snapshot.scenarioName !== persistedSnapshot.scenarioName) {
          setCurrentScenario(persistedSnapshot);
        } else if (currentScenarioMatches && currentScenario) {
          currentScenarioCache = {
            ...currentScenario,
            scenarioId: persistedSnapshot.scenarioId ?? currentScenario.scenarioId,
            currentVersion: persistedSnapshot.currentVersion,
            status: persistedSnapshot.status,
            isDefault: persistedSnapshot.isDefault,
          };
        }
      }

      setCatalogCache(data.pushScenarioSnapshot.products);
      latestConflictDraft = null;
      latestConflictScenarioName = null;
      latestConflictRemoteVersion = null;

      const nextState: RuntimeSyncState = hasPendingScenarioWrites()
        ? {
            status: 'syncing',
            pendingCount: getPendingScenarioWriteCount(),
            conflictCount: 0,
            lastError: null,
            lastSyncedAt: new Date().toISOString(),
            lastConnectivityCheckAt: new Date().toISOString(),
            authRequired: false,
            conflictScenarioName: null,
            conflictRemoteVersion: null,
          }
        : {
            ...defaultSyncState,
            lastSyncedAt: new Date().toISOString(),
            lastConnectivityCheckAt: new Date().toISOString(),
          };
      await persistSyncState(nextState);
      scenarioWriteInFlight = false;

      if (hasPendingScenarioWrites()) {
        return flushPendingScenarioWrite(requestOptions);
      }

      return nextState;
    } catch (error) {
      scenarioWriteInFlight = false;

      if (error instanceof GraphQLRequestError && error.code === 'UNAUTHENTICATED') {
        const currentAuth = await ensureAuthStateLoaded();
        await persistAuthState({
          ...currentAuth,
          authenticated: false,
          authExpired: true,
          sensitiveAccessGranted: false,
          user: null,
          lastValidatedAt: new Date().toISOString(),
        });
        restorePendingScenarioWrites([draft, ...pendingScenarioWrites]);
        const blockedState = buildBlockedSyncState(
          'Authentication expired. Pending changes are kept in this tab until you log in again.',
        );
        await persistSyncState(blockedState);
        return blockedState;
      }

      if (error instanceof GraphQLRequestError && error.code === 'SCENARIO_LOCKED') {
        restorePendingScenarioWrites([draft, ...pendingScenarioWrites]);
        const lockedState = buildLockedSyncState(
          error.message || 'This scenario is currently read-only because another user is editing it.',
        );
        await persistSyncState(lockedState);
        return lockedState;
      }

      if (error instanceof GraphQLRequestError && error.code === 'CONFLICT') {
        const latestDraft = getLatestPendingScenarioWrite()?.snapshot ?? draft.snapshot;
        const scenarioName =
          typeof error.details?.scenarioName === 'string'
            ? error.details.scenarioName
            : draft.snapshot.scenarioName;
        const remoteVersion =
          typeof error.details?.currentVersion === 'number'
            ? error.details.currentVersion
            : null;
        latestConflictDraft = {
          suggestedName: getSuggestedCopyName(latestDraft.scenarioName),
          snapshot: clone(latestDraft),
        };
        latestConflictScenarioName = scenarioName;
        latestConflictRemoteVersion = remoteVersion;
        pendingScenarioWrites = [];
        const conflictState = buildConflictSyncState(
          typeof error.details?.scenarioName === 'string' && remoteVersion != null
            ? `Scenario "${error.details.scenarioName}" already changed in the cloud (version ${remoteVersion}). Load the latest version before saving again.`
            : error.message,
          scenarioName,
          remoteVersion,
        );
        await persistSyncState(conflictState);
        return conflictState;
      }

      restorePendingScenarioWrites([draft, ...pendingScenarioWrites]);
      const failedState = buildFailedSyncState(
        error instanceof Error ? error.message : 'Unknown save failure.',
      );
      await persistSyncState(failedState);
      return failedState;
    }
  })();

  return currentScenarioWritePromise;
}

function scheduleScenarioWrite(): void {
  if (!isBrowser()) {
    void flushPendingScenarioWrite();
    return;
  }

  if (scenarioWriteTimer !== null) {
    window.clearTimeout(scenarioWriteTimer);
  }

  scenarioWriteTimer = window.setTimeout(() => {
    scenarioWriteTimer = null;
    void flushPendingScenarioWrite();
  }, SAVE_DEBOUNCE_MS);
}

export async function loadScenarioNames(): Promise<string[]> {
  const scenarios = await loadScenarioLibrary('ACTIVE');
  return scenarios.map((scenario) => scenario.name);
}

export async function loadScenarioLibrary(status: ScenarioStatus = 'ACTIVE'): Promise<ScenarioSummaryRecord[]> {
  if (scenarioLibraryCache.length === 0) {
    const auth = await ensureAuthStateLoaded();
    if (auth.authenticated) {
      await refreshScenarioLibraryFromServer();
    }
  }

  return scenarioLibraryCache.filter((scenario) => scenario.status === status).map((scenario) => clone(scenario));
}

export async function replaceScenarioLibrary(scenarios: ScenarioSummaryRecord[]): Promise<void> {
  updateScenarioLibraryCache(scenarios);
}

export async function loadScenarioSnapshot(name: string): Promise<ScenarioSnapshot | null> {
  const effectiveDraft = getEffectivePendingDraft();
  if (effectiveDraft?.snapshot.scenarioName === name) {
    return clone(effectiveDraft.snapshot);
  }

  if (currentScenarioCache?.scenarioName === name) {
    return clone(currentScenarioCache);
  }

  const auth = await ensureAuthStateLoaded();
  if (!auth.authenticated) {
    return null;
  }

  const payload = await bootstrapScenarioFromServer(name);
  return mapBootstrapScenarioToSnapshot(payload.scenario);
}

export async function loadActiveScenario(): Promise<ScenarioSnapshot | null> {
  const effectiveDraft = getEffectivePendingDraft();
  if (effectiveDraft) {
    return clone(effectiveDraft.snapshot);
  }

  if (currentScenarioCache) {
    return clone(currentScenarioCache);
  }

  const auth = await ensureAuthStateLoaded();
  if (!auth.authenticated) {
    return null;
  }

  const payload = await bootstrapScenarioFromServer();
  return mapBootstrapScenarioToSnapshot(payload.scenario);
}

export async function persistScenarioSnapshot(snapshot: ScenarioSnapshot): Promise<void> {
  setCurrentScenario(snapshot);
  upsertScenarioSummary({
    id: snapshot.scenarioId ?? snapshot.scenarioName,
    name: snapshot.scenarioName,
    status: snapshot.status,
    isDefault: snapshot.isDefault,
    currentVersion: snapshot.currentVersion ?? 1,
    updatedAt: snapshot.updatedAt,
    hasLocalSnapshot: false,
  });
}

export async function deleteScenarioSnapshot(name: string): Promise<void> {
  if (currentScenarioCache?.scenarioName === name) {
    setCurrentScenario(null);
  }
  removeScenarioSummary(name);
}

export async function loadCatalog(): Promise<MenuItem[]> {
  if (catalogCache.length === 0) {
    const auth = await ensureAuthStateLoaded();
    if (auth.authenticated) {
      await refreshCatalogFromServer();
    }
  }

  return mapProductsToMenuItems(catalogCache).sort((left, right) => left.name.localeCompare(right.name));
}

export async function replaceCatalog(products: ProductRecord[]): Promise<void> {
  setCatalogCache(products);
}

export async function loadAuthState(): Promise<AuthSessionRecord> {
  return { ...(await ensureAuthStateLoaded()) };
}

export async function saveAuthState(state: AuthSessionRecord): Promise<void> {
  await persistAuthState(state);
}

export async function login(email: string, password: string): Promise<AuthSessionRecord> {
  const deviceId = await getOrCreateDeviceId();
  const data = await graphqlRequest<LoginResponse>(LOGIN_MUTATION, {
    input: { email, password, deviceId },
  });
  const nextState: AuthSessionRecord = {
    key: 'session',
    authenticated: data.login.authenticated,
    authExpired: false,
    deviceId,
    authMethod: data.login.authMethod,
    sensitiveAccessGranted: data.login.sensitiveAccessGranted,
    user: data.login.user,
    lastValidatedAt: new Date().toISOString(),
    lastSuccessfulOnlineAuthAt: new Date().toISOString(),
    lastActivityAt: new Date().toISOString(),
  };
  await persistAuthState(nextState);
  await bootstrapScenarioFromServer();
  if (hasPendingScenarioWrites()) {
    await syncPendingMutations(currentScenarioCache?.scenarioName);
  } else {
    await setSyncStateIdle();
  }
  return nextState;
}

export async function loginWithPin(email: string, pin: string): Promise<AuthSessionRecord> {
  const deviceId = await getOrCreateDeviceId();
  const data = await graphqlRequest<LoginWithPinResponse>(LOGIN_WITH_PIN_MUTATION, {
    input: { email, pin, deviceId },
  });
  const nextState: AuthSessionRecord = {
    key: 'session',
    authenticated: data.loginWithPin.authenticated,
    authExpired: false,
    deviceId,
    authMethod: data.loginWithPin.authMethod,
    sensitiveAccessGranted: data.loginWithPin.sensitiveAccessGranted,
    user: data.loginWithPin.user,
    lastValidatedAt: new Date().toISOString(),
    lastSuccessfulOnlineAuthAt: new Date().toISOString(),
    lastActivityAt: new Date().toISOString(),
  };
  await persistAuthState(nextState);
  await bootstrapScenarioFromServer();
  if (hasPendingScenarioWrites()) {
    await syncPendingMutations(currentScenarioCache?.scenarioName);
  } else {
    await setSyncStateIdle();
  }
  return nextState;
}

export async function logout(): Promise<void> {
  try {
    await graphqlRequest<{ logout: boolean }>(LOGOUT_MUTATION);
  } catch {
    // Local cleanup still matters when closing the session.
  }

  const deviceId = (await ensureAuthStateLoaded()).deviceId;
  await persistAuthState(defaultAuthState(deviceId));
  setCurrentScenario(null);
  updateScenarioLibraryCache([]);
  setCatalogCache([]);
  latestConflictDraft = null;
  latestConflictScenarioName = null;
  latestConflictRemoteVersion = null;
  pendingScenarioWrites = [];
  await persistSyncState({ ...defaultSyncState });
}

export async function updateProfile(input: {
  email: string;
  displayName: string;
  currentPassword?: string;
  password?: string;
  pin?: string;
}): Promise<AuthSessionRecord> {
  const data = await graphqlRequest<UpdateProfileResponse>(UPDATE_PROFILE_MUTATION, { input });
  const current = await ensureAuthStateLoaded();
  const nextState: AuthSessionRecord = {
    ...current,
    authenticated: true,
    authExpired: false,
    user: data.updateProfile,
    sensitiveAccessGranted: true,
    lastValidatedAt: new Date().toISOString(),
    lastActivityAt: new Date().toISOString(),
  };
  await persistAuthState(nextState);
  return nextState;
}

export async function updateUserPreferences(input: UserPreferencesUpdateInput): Promise<AuthSessionRecord> {
  const data = await graphqlRequest<UpdateUserPreferencesResponse>(UPDATE_USER_PREFERENCES_MUTATION, { input });
  const current = await ensureAuthStateLoaded();
  const nextState: AuthSessionRecord = {
    ...current,
    user: data.updateUserPreferences,
    lastValidatedAt: new Date().toISOString(),
    lastActivityAt: new Date().toISOString(),
  };
  await persistAuthState(nextState);
  return nextState;
}

export async function bootstrapFromServer(scenarioName?: string): Promise<BootstrapPayload> {
  return bootstrapScenarioFromServer(scenarioName);
}

export async function refreshScenarioLibraryFromServer(status?: ScenarioStatus): Promise<ScenarioSummaryRecord[]> {
  let data: ScenarioLibraryResponse;
  try {
    data = await graphqlRequest<ScenarioLibraryResponse>(SCENARIO_LIBRARY_QUERY, status ? { status } : undefined);
  } catch (error) {
    if (error instanceof GraphQLRequestError && error.code === 'UNAUTHENTICATED') {
      await persistExpiredAuthState();
    }
    throw error;
  }
  if (!status) {
    updateScenarioLibraryCache(data.scenarioLibrary.map(mapScenarioSummary));
    return scenarioLibraryCache.map((scenario) => clone(scenario));
  }

  const incoming = data.scenarioLibrary.map(mapScenarioSummary);
  updateScenarioLibraryCache([
    ...scenarioLibraryCache.filter((scenario) => scenario.status !== status),
    ...incoming,
  ]);
  return incoming;
}

export async function refreshCatalogFromServer(): Promise<ProductRecord[]> {
  let data: ProductsResponse;
  try {
    data = await graphqlRequest<ProductsResponse>(PRODUCTS_QUERY);
  } catch (error) {
    if (error instanceof GraphQLRequestError && error.code === 'UNAUTHENTICATED') {
      await persistExpiredAuthState();
    }
    throw error;
  }
  setCatalogCache(data.products);
  return clone(data.products);
}

export async function loadScenarioEditPresence(scenarioName: string): Promise<ScenarioEditPresenceRecord> {
  const data = await graphqlRequest<ScenarioEditPresenceResponse>(SCENARIO_EDIT_PRESENCE_QUERY, { scenarioName });
  return data.scenarioEditPresence;
}

export async function loadScenarioHistory(scenarioName: string): Promise<ScenarioHistoryStateRecord> {
  const data = await graphqlRequest<ScenarioHistoryResponse>(SCENARIO_HISTORY_QUERY, { scenarioName });
  return clone(data.scenarioHistory);
}

async function applyScenarioHistoryMutationPayload(
  payload: ScenarioHistoryMutationPayload,
): Promise<{ scenario: ScenarioSnapshot | null; history: ScenarioHistoryStateRecord }> {
  const snapshot = mapBootstrapScenarioToSnapshot(payload.scenario);
  if (snapshot) {
    await persistScenarioSnapshot(snapshot);
  }

  return {
    scenario: snapshot,
    history: clone(payload.history),
  };
}

export async function undoScenarioHistory(
  scenarioName: string,
): Promise<{ scenario: ScenarioSnapshot | null; history: ScenarioHistoryStateRecord }> {
  const syncResult = await syncPendingMutations(scenarioName);
  if (syncResult.status !== 'idle') {
    throw new Error(syncResult.lastError ?? 'Unable to sync pending changes before undo.');
  }
  const data = await graphqlRequest<UndoScenarioHistoryResponse>(UNDO_SCENARIO_HISTORY_MUTATION, { scenarioName });
  return applyScenarioHistoryMutationPayload(data.undoScenarioHistory);
}

export async function redoScenarioHistory(
  scenarioName: string,
): Promise<{ scenario: ScenarioSnapshot | null; history: ScenarioHistoryStateRecord }> {
  const syncResult = await syncPendingMutations(scenarioName);
  if (syncResult.status !== 'idle') {
    throw new Error(syncResult.lastError ?? 'Unable to sync pending changes before redo.');
  }
  const data = await graphqlRequest<RedoScenarioHistoryResponse>(REDO_SCENARIO_HISTORY_MUTATION, { scenarioName });
  return applyScenarioHistoryMutationPayload(data.redoScenarioHistory);
}

export async function acquireScenarioEditLock(
  scenarioName: string,
  options?: { force?: boolean },
): Promise<ScenarioEditPresenceRecord> {
  const data = await graphqlRequest<AcquireScenarioEditLockResponse>(ACQUIRE_SCENARIO_EDIT_LOCK_MUTATION, {
    scenarioName,
    force: options?.force ?? false,
  });
  return data.acquireScenarioEditLock;
}

export async function renewScenarioEditLock(scenarioName: string): Promise<ScenarioEditPresenceRecord> {
  const data = await graphqlRequest<RenewScenarioEditLockResponse>(RENEW_SCENARIO_EDIT_LOCK_MUTATION, { scenarioName });
  return data.renewScenarioEditLock;
}

export async function releaseScenarioEditLock(
  scenarioName: string,
  options?: GraphQLRequestOptions,
): Promise<ScenarioEditPresenceRecord> {
  const data = await graphqlRequest<ReleaseScenarioEditLockResponse>(
    RELEASE_SCENARIO_EDIT_LOCK_MUTATION,
    { scenarioName },
    options,
  );
  return data.releaseScenarioEditLock;
}

export async function setScenarioReleasePending(
  scenarioName: string,
  active: boolean,
): Promise<ScenarioEditPresenceRecord> {
  const data = await graphqlRequest<SetScenarioReleasePendingResponse>(SET_SCENARIO_RELEASE_PENDING_MUTATION, {
    scenarioName,
    active,
  });
  return data.setScenarioReleasePending;
}

export async function requestScenarioEditAccess(scenarioName: string): Promise<ScenarioEditPresenceRecord> {
  const data = await graphqlRequest<RequestScenarioEditAccessResponse>(REQUEST_SCENARIO_EDIT_ACCESS_MUTATION, {
    scenarioName,
  });
  return data.requestScenarioEditAccess;
}

export async function respondScenarioEditAccessRequest(
  scenarioName: string,
  approve: boolean,
  deniedReason?: string | null,
): Promise<ScenarioEditPresenceRecord> {
  const data = await graphqlRequest<RespondScenarioEditAccessRequestResponse>(
    RESPOND_SCENARIO_EDIT_ACCESS_REQUEST_MUTATION,
    {
      scenarioName,
      approve,
      deniedReason: deniedReason ?? null,
    },
  );
  return data.respondScenarioEditAccessRequest;
}

export async function revalidateAuth(): Promise<AuthSessionRecord> {
  try {
    const data = await graphqlRequest<RevalidateResponse>(REVALIDATE_SESSION_MUTATION);
    return normalizeAuthSessionFromResponse(data.revalidateSession);
  } catch (error) {
    if (error instanceof GraphQLRequestError && error.code === 'UNAUTHENTICATED') {
      return persistExpiredAuthState();
    }
    throw error;
  }
}


export function persistScenarioAndQueue(
  snapshot: ScenarioSnapshot,
  options?: { previousScenarioName?: string | null; historyDescription?: string | null },
): Promise<void> {
  const mergedSnapshot: ScenarioSnapshot = {
    ...(currentScenarioCache?.scenarioName === (options?.previousScenarioName ?? snapshot.scenarioName)
      ? currentScenarioCache
      : {}),
    ...snapshot,
    updatedAt: new Date().toISOString(),
  };

  const nextWrite: PendingScenarioWrite = {
    snapshot: clone(mergedSnapshot),
    previousScenarioName: options?.previousScenarioName ?? null,
    forceOverwrite: false,
    historyDescription: options?.historyDescription?.trim() || null,
  };

  const nextPendingWrites = [...pendingScenarioWrites];
  const trailingWrite = nextPendingWrites[nextPendingWrites.length - 1];
  if (
    trailingWrite &&
    trailingWrite.snapshot.scenarioName === nextWrite.snapshot.scenarioName &&
    trailingWrite.previousScenarioName === nextWrite.previousScenarioName &&
    !trailingWrite.forceOverwrite &&
    !trailingWrite.historyDescription &&
    !nextWrite.historyDescription
  ) {
    nextPendingWrites[nextPendingWrites.length - 1] = nextWrite;
  } else if (
    trailingWrite &&
    trailingWrite.snapshot.scenarioName === nextWrite.snapshot.scenarioName &&
    trailingWrite.previousScenarioName === nextWrite.previousScenarioName &&
    !trailingWrite.forceOverwrite &&
    !trailingWrite.historyDescription &&
    Boolean(nextWrite.historyDescription)
  ) {
    nextPendingWrites[nextPendingWrites.length - 1] = nextWrite;
  } else {
    nextPendingWrites.push(nextWrite);
  }
  pendingScenarioWrites = nextPendingWrites;

  if (options?.previousScenarioName && options.previousScenarioName !== mergedSnapshot.scenarioName) {
    removeScenarioSummary(options.previousScenarioName);
  }

  setCurrentScenario(mergedSnapshot);
  upsertScenarioSummary({
    id: mergedSnapshot.scenarioId ?? mergedSnapshot.scenarioName,
    name: mergedSnapshot.scenarioName,
    status: mergedSnapshot.status,
    isDefault: mergedSnapshot.isDefault,
    currentVersion: lastSyncedVersionByScenario.get(mergedSnapshot.scenarioName) ?? mergedSnapshot.currentVersion ?? 1,
    updatedAt: mergedSnapshot.updatedAt,
    hasLocalSnapshot: false,
  });
  void persistSyncState({
    status: 'syncing',
    pendingCount: getPendingScenarioWriteCount(),
    conflictCount: 0,
    lastError: null,
    lastSyncedAt: syncStateCache.lastSyncedAt,
    lastConnectivityCheckAt: new Date().toISOString(),
    authRequired: false,
    conflictScenarioName: null,
    conflictRemoteVersion: null,
  });
  scheduleScenarioWrite();
  return Promise.resolve();
}

export async function flushScenarioWrites(): Promise<void> {
  await flushPendingScenarioWrite();
}

export async function updateScenarioSettings(input: {
  currentName: string;
  nextName: string;
  gridCols: number;
  gridRows: number;
  isDefault: boolean;
  showPrices: boolean;
  itemizePrintItems: boolean;
}): Promise<ScenarioSnapshot> {
  const data = await graphqlRequest<UpdateScenarioResponse>(UPDATE_SCENARIO_MUTATION, { input });
  const snapshot = mapBootstrapScenarioToSnapshot(data.updateScenario);
  if (!snapshot) {
    throw new Error('Scenario update returned no scenario snapshot.');
  }

  if (input.currentName !== snapshot.scenarioName) {
    removeScenarioSummary(input.currentName);
  }
  await persistScenarioSnapshot(snapshot);
  return snapshot;
}

export async function loadScenarioBaseState(name: string): Promise<ScenarioBaseStateRecord | null> {
  const data = await graphqlRequest<ScenarioBaseStateResponse>(SCENARIO_BASE_STATE_QUERY, { scenarioName: name });
  return data.scenarioBaseState ? clone(data.scenarioBaseState) : null;
}

export async function saveScenarioBaseState(name: string): Promise<ScenarioSnapshot> {
  const data = await graphqlRequest<SaveScenarioBaseStateResponse>(SAVE_SCENARIO_BASE_STATE_MUTATION, { name });
  const snapshot = mapBootstrapScenarioToSnapshot(data.saveScenarioBaseState);
  if (!snapshot) {
    throw new Error('Saving scenario base state returned no scenario snapshot.');
  }

  await persistScenarioSnapshot(snapshot);
  return snapshot;
}

export async function setScenarioArchived(name: string, archived: boolean): Promise<ScenarioSnapshot | null> {
  const payload = archived
    ? (await graphqlRequest<ArchiveScenarioResponse>(ARCHIVE_SCENARIO_MUTATION, { name })).archiveScenario
    : (await graphqlRequest<RestoreScenarioResponse>(RESTORE_SCENARIO_MUTATION, { name })).restoreScenario;
  const snapshot = mapBootstrapScenarioToSnapshot(payload);
  if (!snapshot) {
    return null;
  }

  await persistScenarioSnapshot(snapshot);
  return snapshot;
}

export async function deleteScenario(name: string): Promise<void> {
  await graphqlRequest<DeleteScenarioResponse>(DELETE_SCENARIO_MUTATION, { name });
  if (currentScenarioCache?.scenarioName === name) {
    setCurrentScenario(null);
  }
  removeScenarioSummary(name);
  pendingScenarioWrites = pendingScenarioWrites.filter((write) => write.snapshot.scenarioName !== name);
}

export function syncPendingMutations(
  _scenarioName?: string,
  requestOptions?: GraphQLRequestOptions,
): Promise<RuntimeSyncState> {
  return flushPendingScenarioWrite(requestOptions);
}

export async function resolveLatestScenarioConflict(): Promise<ScenarioSnapshot | null> {
  if (!latestConflictDraft || !latestConflictScenarioName) {
    await setSyncStateIdle();
    return loadActiveScenario();
  }

  const scenarioName = latestConflictScenarioName;
  latestConflictDraft = null;
  latestConflictScenarioName = null;
  latestConflictRemoteVersion = null;
  await bootstrapScenarioFromServer(scenarioName);
  await setSyncStateIdle();
  return loadScenarioSnapshot(scenarioName);
}

export async function getLatestConflictScenarioDraft(): Promise<ConflictScenarioDraft | null> {
  if (!latestConflictDraft) {
    return null;
  }

  return {
    suggestedName: getSuggestedCopyName(latestConflictDraft.snapshot.scenarioName),
    snapshot: clone(latestConflictDraft.snapshot),
  };
}

export async function keepLatestConflictAsScenarioCopy(
  preferredName?: string,
  overrides?: { showPrices?: boolean; itemizePrintItems?: boolean },
): Promise<ScenarioSnapshot | null> {
  if (!latestConflictDraft) {
    await setSyncStateIdle();
    return null;
  }

  const nextName = preferredName?.trim() || getSuggestedCopyName(latestConflictDraft.snapshot.scenarioName);
  if (scenarioLibraryCache.some((scenario) => scenario.name === nextName)) {
    throw new Error(`Scenario "${nextName}" already exists.`);
  }

  const copySnapshot: ScenarioSnapshot = {
    ...clone(latestConflictDraft.snapshot),
    scenarioId: undefined,
    scenarioName: nextName,
    status: 'ACTIVE',
    isDefault: false,
    showPrices: overrides?.showPrices ?? latestConflictDraft.snapshot.showPrices,
    itemizePrintItems: overrides?.itemizePrintItems ?? latestConflictDraft.snapshot.itemizePrintItems,
    currentVersion: undefined,
    updatedAt: new Date().toISOString(),
  };

  latestConflictDraft = null;
  latestConflictScenarioName = null;
  latestConflictRemoteVersion = null;
  persistScenarioAndQueue(copySnapshot);
  const syncResult = await syncPendingMutations(copySnapshot.scenarioName);
  if (syncResult.status !== 'idle') {
    throw new Error(syncResult.lastError ?? 'Unable to save the copied scenario.');
  }
  return loadScenarioSnapshot(copySnapshot.scenarioName);
}

export async function overwriteLatestScenarioConflictWithLocalSnapshot(): Promise<ScenarioSnapshot | null> {
  if (!latestConflictDraft) {
    await setSyncStateIdle();
    return null;
  }

  pendingScenarioWrites = [{
    snapshot: clone(latestConflictDraft.snapshot),
    previousScenarioName: latestConflictScenarioName,
    forceOverwrite: true,
    historyDescription: null,
  }];
  latestConflictDraft = null;
  latestConflictScenarioName = null;
  latestConflictRemoteVersion = null;
  const targetScenarioName = pendingScenarioWrites[0].snapshot.scenarioName;
  const syncResult = await syncPendingMutations(targetScenarioName);
  if (syncResult.status !== 'idle') {
    throw new Error(syncResult.lastError ?? 'Unable to replace the cloud version with your changes.');
  }
  return loadActiveScenario();
}

export async function loadSyncState(): Promise<RuntimeSyncState> {
  return { ...syncStateCache };
}

export async function touchSessionActivity(): Promise<AuthSessionRecord> {
  const current = await ensureAuthStateLoaded();
  if (!current.authenticated || current.authExpired) {
    return current;
  }
  const nextState: AuthSessionRecord = {
    ...current,
    lastActivityAt: new Date().toISOString(),
  };
  await persistAuthState(nextState);
  return nextState;
}

export async function expireAuthSession(): Promise<AuthSessionRecord> {
  const current = await ensureAuthStateLoaded();
  const nextState: AuthSessionRecord = {
    ...current,
    authenticated: false,
    authExpired: true,
    sensitiveAccessGranted: false,
    lastValidatedAt: new Date().toISOString(),
  };
  await persistAuthState(nextState);
  if (hasPendingScenarioWrites()) {
    await persistSyncState(buildBlockedSyncState('Authentication expired. Re-login is required before saving can continue.'));
  }
  return nextState;
}

export async function authorizeSensitiveAccess(password: string): Promise<AuthSessionRecord> {
  const data = await graphqlRequest<AuthorizeSensitiveAccessResponse>(AUTHORIZE_SENSITIVE_ACCESS_MUTATION, { password });
  return normalizeAuthSessionFromResponse(data.authorizeSensitiveAccess);
}
